import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchCatererClaimRequests, approveCatererClaimRequest, rejectCatererClaimRequest } from '../../lib/adminApi';

export default function CatererClaimsPage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCatererClaimRequests({ status: statusFilter === 'all' ? undefined : statusFilter });
        if (isMounted) {
          setRequests(data);
        }
      } catch (err) {
        console.error('Failed to load claim requests', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load claim requests');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [statusFilter]);

  const handleApprove = async (req) => {
    if (!profile?.user_id) return;
    setProcessingId(req.id);
    try {
      await approveCatererClaimRequest(req.id, profile.user_id);
      setRequests((prev) =>
        prev.map((r) =>
          r.id === req.id
            ? { ...r, status: 'approved', reviewed_at: new Date().toISOString(), reviewed_by_user_id: profile.user_id }
            : r
        )
      );
      toast.success('Claim approved and caterer marked as claimed');
    } catch (err) {
      console.error('Failed to approve claim', err);
      toast.error(err instanceof Error ? err.message : 'Failed to approve claim');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req) => {
    if (!profile?.user_id) return;
    const reason = window.prompt('Reason for rejecting this claim? (optional)');
    setProcessingId(req.id);
    try {
      await rejectCatererClaimRequest(req.id, profile.user_id, reason || null);
      setRequests((prev) =>
        prev.map((r) =>
          r.id === req.id
            ? {
                ...r,
                status: 'rejected',
                reviewed_at: new Date().toISOString(),
                reviewed_by_user_id: profile.user_id,
                decision_notes: reason || null,
              }
            : r
        )
      );
      toast.info('Claim rejected');
    } catch (err) {
      console.error('Failed to reject claim', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reject claim');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Business Claim Approvals</h2>
          <p className="text-sm text-muted-foreground">
            Review requests from foodpreneurs who want to claim existing listings on Mrs Cater.
          </p>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Icon name="UserCheck" size={18} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {requests.length} request{requests.length === 1 ? '' : 's'} in view
            </p>
          </div>
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map((value) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                  statusFilter === value
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {value === 'all' ? 'All' : value.charAt(0).toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Business</th>
                <th className="px-4 py-3 text-left font-semibold">Requester</th>
                <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Notes</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-6 text-center text-muted-foreground">
                    Loading claim requests…
                  </td>
                </tr>
              )}

              {!loading && requests.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-6 text-center text-muted-foreground">
                    No claim requests for this filter.
                  </td>
                </tr>
              )}

              {requests.map((req) => (
                <tr key={req.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-foreground">
                        {req.caterer?.business_name || 'Unknown caterer'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Caterer ID: {req.caterer_id?.slice(0, 8) || '—'}…
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      <span>{req.requester_name || '—'}</span>
                      <span>{req.requester_email || '—'}</span>
                      <span className="text-xs">{req.requester_phone || ''}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {req.created_at ? format(new Date(req.created_at), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${
                        req.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-700'
                          : req.status === 'rejected'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {req.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">
                    {req.message && (
                      <p className="truncate" title={req.message}>
                        {req.message}
                      </p>
                    )}
                    {req.decision_notes && (
                      <p className="mt-1 truncate" title={req.decision_notes}>
                        Admin: {req.decision_notes}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() =>
                          window.open(
                            `mailto:${req.requester_email || ''}?subject=${encodeURIComponent(
                              `Regarding your claim for ${req.caterer?.business_name || 'your business'}`
                            )}`,
                            '_blank'
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="Mail" size={14} /> Email
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button
                            disabled={processingId === req.id}
                            onClick={() => handleApprove(req)}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                          >
                            <Icon name="CheckCircle" size={14} /> Approve
                          </button>
                          <button
                            disabled={processingId === req.id}
                            onClick={() => handleReject(req)}
                            className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                          >
                            <Icon name="XCircle" size={14} /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

